// Wires the multi-file course-attachments form. Per picked file:
//   1. Render a row with the filename and an editable title input
//      (defaulted to the filename minus extension).
//   2. Sign + PUT each file on pick so titles edit in parallel;
//      submit enables once every upload is ready.
//   3. On submit, POST CreateCourseDocument once per row with that
//      row's title + storage_key, then reload the page so the
//      attachments card reflects the new state.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

export function initAcademyAttachmentsForm(root = document) {
  const forms = root.querySelectorAll("[data-academy-attachments-form]");
  forms.forEach((form) => {
    if (!firstBind(form)) return;
    wireForm(form);
  });
}

function wireForm(form) {
  const signEndpoint = form.getAttribute("data-sign-endpoint");
  const createEndpoint = form.getAttribute("data-create-endpoint");
  const input = form.querySelector("[data-academy-attachments-input]");
  const dropzone = form.querySelector("[data-academy-attachments-dropzone]");
  const list = form.querySelector("[data-academy-attachments-rows]");
  const status = form.querySelector("[data-academy-attachments-status]");
  const submit = form.querySelector("[data-academy-attachments-submit]");
  if (!input || !dropzone || !list || !status || !submit) return;

  /** @type {Row[]} */
  const rows = [];
  let submitting = false;

  const setStatus = (text) => {
    status.textContent = text;
    status.classList.toggle("hidden", !text);
  };

  const refreshSubmit = () => {
    if (rows.length === 0 || submitting) {
      submit.disabled = true;
      return;
    }
    submit.disabled = rows.some((r) => !r.ready);
  };

  const removeRow = (row) => {
    const i = rows.indexOf(row);
    if (i === -1) return;
    rows.splice(i, 1);
    row.el.remove();
    if (rows.length === 0) list.classList.add("hidden");
    refreshSubmit();
  };

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    setStatus("");
    Array.from(files).forEach((file) => {
      const row = createRow(file, () => removeRow(row));
      rows.push(row);
      list.appendChild(row.el);
      list.classList.remove("hidden");
      refreshSubmit();
      uploadRow(row, signEndpoint).then(refreshSubmit);
    });
    input.value = "";
  };

  input.addEventListener("change", () => handleFiles(input.files));
  ["dragenter", "dragover"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add("ring-2", "ring-brand-500/40");
    }),
  );
  ["dragleave", "drop"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.remove("ring-2", "ring-brand-500/40");
    }),
  );
  dropzone.addEventListener("drop", (e) => handleFiles(e.dataTransfer?.files));

  submit.addEventListener("click", async (e) => {
    e.preventDefault();
    if (submitting || rows.length === 0) return;
    if (rows.some((r) => !r.ready)) return;
    submitting = true;
    submit.textContent = "Attaching…";
    refreshSubmit();
    setStatus("");

    try {
      for (const row of rows) {
        await createDocument(createEndpoint, row);
      }
      // Brief success confirmation before the reload so the user sees
      // their action register — the reload itself is the canonical
      // "the new docs are now in the list" feedback, but on a fast
      // connection that swap is too quick to read as success.
      const count = rows.length;
      submit.textContent =
        count === 1 ? "Attached. Reloading…" : `Attached ${count}. Reloading…`;
      window.setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      console.error("academy.attachments create failed", err);
      setStatus(err.message || "Could not attach the files. Please try again.");
      submit.textContent = "Attach files";
      submitting = false;
      refreshSubmit();
    }
  });
}

/**
 * @typedef {Object} Row
 * @property {HTMLLIElement} el
 * @property {File} file
 * @property {HTMLInputElement} titleInput
 * @property {HTMLElement} statusEl
 * @property {string} storageKey
 * @property {boolean} ready
 */

/** @returns {Row} */
function createRow(file, onRemove) {
  const el = document.createElement("li");
  el.className =
    "rounded-lg border border-orange-100 bg-white px-3 py-2.5 space-y-2";

  // Header row: filename + upload status pill + remove button.
  const header = document.createElement("div");
  header.className = "flex items-center gap-2";

  const filename = document.createElement("span");
  filename.className = "flex-1 min-w-0 truncate text-xs text-ink-500";
  filename.title = file.name;
  filename.textContent = file.name;

  const statusEl = document.createElement("span");
  statusEl.className =
    "text-[11px] font-medium text-ink-500 shrink-0 transition-colors duration-150 ease-out";
  statusEl.textContent = "Uploading…";

  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.setAttribute("aria-label", "Remove " + file.name);
  removeBtn.className =
    "text-ink-500 hover:text-red-600 shrink-0 text-base leading-none transition-colors duration-150 ease-out active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded";
  removeBtn.textContent = "×";
  removeBtn.addEventListener("click", onRemove);

  header.appendChild(filename);
  header.appendChild(statusEl);
  header.appendChild(removeBtn);

  // Title field: labelled, full-width, hard to miss.
  const titleLabel = document.createElement("label");
  titleLabel.className = "block";

  const titleLabelText = document.createElement("span");
  titleLabelText.className =
    "block text-[10px] font-semibold uppercase tracking-wider text-ink-500";
  titleLabelText.textContent = "Display title";

  const titleInput = document.createElement("input");
  titleInput.type = "text";
  titleInput.value = defaultTitle(file.name);
  titleInput.required = true;
  titleInput.maxLength = 120;
  titleInput.placeholder = "How the file is labelled in the attachments list";
  titleInput.className =
    "block w-full px-2.5 py-1.5 mt-1 bg-white border border-orange-200 rounded-md text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors";

  titleLabel.appendChild(titleLabelText);
  titleLabel.appendChild(titleInput);

  el.appendChild(header);
  el.appendChild(titleLabel);

  return { el, file, titleInput, statusEl, storageKey: "", ready: false };
}

function defaultTitle(filename) {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
}

async function uploadRow(row, signEndpoint) {
  try {
    const signed = await sign(signEndpoint, row.file);
    await put(signed.upload_url, row.file, signed.upload_headers);
    row.storageKey = signed.key;
    row.ready = true;
    row.statusEl.textContent = "Ready";
    row.statusEl.className = "text-xs text-emerald-600 shrink-0";
  } catch (err) {
    console.error("academy.attachments upload failed", err);
    row.ready = false;
    row.statusEl.textContent = err.message || "Failed";
    row.statusEl.className = "text-xs text-red-600 shrink-0";
  }
}

async function sign(endpoint, file) {
  const body = new URLSearchParams();
  body.set("filename", file.name);
  body.set("content_type", file.type || "application/octet-stream");
  body.set("file_size_bytes", String(file.size));

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const payload = await safeJSON(res);
    throw new Error(payload?.error || `Sign failed (${res.status})`);
  }
  return res.json();
}

async function put(url, file, headers) {
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(headers || {}),
    },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

async function createDocument(endpoint, row) {
  const body = new URLSearchParams();
  body.set("title", row.titleInput.value.trim() || row.file.name);
  body.set("storage_key", row.storageKey);

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok && res.status >= 300 && res.status < 400) return;
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Attach failed (${res.status})`);
  }
}

async function safeJSON(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}
